import React, { useMemo, useCallback, useEffect, useState } from 'react';
import _ from 'lodash';

import { hasValidInput } from 'components/Model/ModelUtils';
import ModelType from 'components/Model/ModelType';
import ModelsContainer from 'components/Model/ModelsContainer';
import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';
import { useTranslation } from 'localization/useTranslation';

const DataSourceAsset = ({ assetInfo, setAssetInfo, addErrorAlert, setIsValid }) => {
  const T = useTranslation();
  const api = useAPI();
  const [bindings, setBindings] = useState();
  const [sourceAssets, setSourceAssets] = useState();

  const [getBindingDescription] = useApiCall({
    apiCall: api.core.getBinding,
    onSuccess: useCallback((model) => {
      setAssetInfo(oldAssetInfo => ({ ...oldAssetInfo, assetData: { ...oldAssetInfo.assetData, bindingModel: model } }));
    }, [setAssetInfo]),
    onError: useCallback((error) => {
      addErrorAlert(`Failed to fetch binding model ${error}`);
    }, [addErrorAlert])
  });

  const [getSourceAssets, isLoadingAssets] = useApiCall({
    apiCall: api.core.getAssetsByModels,
    onSuccess: useCallback((assets) => {
      setSourceAssets(assets);
    }, []),
    onError: useCallback((errors) => {
      console.error(errors);
    }, [])
  });

  const [getBindings, isLoadingBindings] = useApiCall({
    apiCall: api.core.getBindingsByModelId,
    onSuccess: useCallback((bindings) => {
      setBindings(bindings);
      const sources = _.uniq(_.compact(_.flatten(_.map(bindings, binding => binding.sources))));
      getSourceAssets(sources);
    }, [getSourceAssets]),
    onError: useCallback((error) => {
      addErrorAlert(`Failed to fetch binding model ${error}`);
    }, [addErrorAlert])
  });

  useEffect(function updateBindingList() {
    if (!_.isEmpty(assetInfo.asset?.model)) {
      getBindings(assetInfo.asset?.model?.id);
    }
  }, [assetInfo.asset?.model, getBindings]);

  const assetModel = useMemo(() => {
    return [
      {
        type: ModelType.OPTIONSV2,
        label: T.admin.createAsset.selectSource,
        path: 'assetData.source',
        isRequired: true,
        hasError: _.isEmpty,
        options: _.map(sourceAssets, asset => ({ value: asset, text: asset.displayName, description: asset.modelId })),
        properties: {
          isLoading: isLoadingAssets,
          iteratee: _.iteratee('id'),
          isTypeahead: true
        }
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.admin.createAsset.selectBinding,
        path: 'assetData.binding',
        isRequired: true,
        hasError: _.isEmpty,
        options: _.map(bindings, binding => ({ value: binding, text: binding.displayName, description: binding.id })),
        properties: {
          isLoading: isLoadingBindings,
          iteratee: _.iteratee('id'),
          isTypeahead: true
        }
      }
    ];
  }, [T.admin.createAsset.selectBinding, T.admin.createAsset.selectSource, bindings, isLoadingAssets, isLoadingBindings, sourceAssets]);

  useEffect(function onBindingDescriptionChange() {
    if (!_.isEmpty(assetInfo?.assetData?.binding)) {
      getBindingDescription(assetInfo?.assetData?.binding.id);
    }
  }, [assetInfo?.assetData?.binding, getBindingDescription]);

  useEffect(function validateInput () {
    setIsValid(hasValidInput(assetInfo, assetModel));
  }, [assetInfo, assetModel, setIsValid]);

  return <ModelsContainer models={assetModel} data={assetInfo} setData={setAssetInfo} />;
};

export default DataSourceAsset;
